import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchReset } from '../../actions/fetch.actions';
import { INPUT_STATES } from '../../components/forms/forms.utils';
import TextArea from '../../components/forms/TextArea';
import TextField from '../../components/forms/TextField';
import formValidators from '../../form-validators';
import { useForm } from '../../hooks/useForm.hook';
import { getCanSubmitPartner } from '../../lib/partner.utils';
import { ADMIN_PARTNERS_RESOURCE } from '../../resources/admin/admin.partners.resource';
import partnersService from '../../services/api/partners.service';
import Modal from '../Modal';
import ModalTitle from '../ModalTitle';

const INPUT_NAME = 'spo-add-partner-textfield';
const DEFAULT_FORM = {
  name: '',
  postcode: '',
  city: '',
  phone: '',
  streetName: '',
  houseNumber: '',
  province: '',
  email: '',
  remarks: ''
};

const AddPartnerModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const modalContentRef = useRef(null);
  const [validation, setValidation] = useState({});
  const { data, isSubmitting, input, load, submitStart, submitOk, submitError, error } = useForm({ ...DEFAULT_FORM });

  const validators = {
    email: formValidators.emailNonRequired,
    phone: formValidators.phoneSimple
  };

  const handleConfirm = () => {
    submitStart();
    partnersService.add(
      { ...data },
      {
        onSuccess: () => {
          submitOk();
          dispatch(fetchReset(ADMIN_PARTNERS_RESOURCE));
          handleClose();
        },
        onError: (message) => {
          submitError(message);
          if (modalContentRef.current) {
            modalContentRef.current.scrollTop = modalContentRef.current.scrollHeight;
          }
        }
      }
    );
  };

  const handleClose = () => {
    load({ ...DEFAULT_FORM });
    onClose();
  };

  const handleEnter = (event) => {
    if (event.code === 'Enter' && getCanSubmitPartner(data, validation)) {
      handleConfirm();
    }
  };

  const handleInput = (value, field) => {
    if (validators[field]) {
      setValidation({ ...validation, [field]: validators[field](value) });
    }
    input(value, field);
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        document.getElementsByName(INPUT_NAME)?.[0]?.focus();
      }, 10);
    }
  }, [isOpen]);

  return (
    <Modal
      ref={modalContentRef}
      className="spo-o-add-partner-modal"
      title={<ModalTitle hasError={!!error} title="Partner toevoegen" />}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      canConfirm={getCanSubmitPartner(data, validation)}
      isSubmitting={isSubmitting}
      onConfirmLabel="Toevoegen"
      onConfirmIcon="ai-check-1"
    >
      <TextField
        className="u-margin-top-xs"
        label="Naam"
        name={'name'}
        value={data?.name}
        required
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        onChange={(value) => handleInput(value, 'name')}
        disabled={isSubmitting}
        hasError={!!validation?.name}
        validationError={validation?.name ?? ''}
      />
      <TextField
        label="Postcode"
        name={'postcode'}
        value={data?.postcode}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        onChange={(value) => handleInput(value, 'postcode')}
        disabled={isSubmitting}
      />
      <TextField
        label="Gemeente"
        name={'city'}
        value={data?.city}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        onChange={(value) => handleInput(value, 'city')}
        disabled={isSubmitting}
      />
      <TextField
        label="Straatnaam"
        name={'streetName'}
        value={data?.streetName}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        onChange={(value) => handleInput(value, 'streetName')}
        disabled={isSubmitting}
      />
      <TextField
        label="Huisnummer"
        name={'houseNumber'}
        value={data?.houseNumber}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        onChange={(value) => handleInput(value, 'houseNumber')}
        disabled={isSubmitting}
      />
      <TextField
        label="Provincie"
        name={'province'}
        value={data?.province}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        onChange={(value) => handleInput(value, 'province')}
        disabled={isSubmitting}
      />
      <TextField
        label="Telefoon"
        name={'phone'}
        value={data?.phone}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        onChange={(value) => handleInput(value, 'phone')}
        disabled={isSubmitting}
        hasError={!!validation?.phone}
        validationError={validation?.phone ?? ''}
      />
      <TextField
        label="E-mail"
        name={'email'}
        value={data?.email}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        onChange={(value) => handleInput(value, 'email')}
        disabled={isSubmitting}
        hasError={!!validation?.email}
        validationError={validation?.email ?? ''}
      />
      <TextArea
        label="Opmerkingen"
        name={INPUT_NAME}
        value={data?.remarks}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        className="u-margin-top"
        onChange={(value) => handleInput(value, 'remarks')}
        disabled={isSubmitting}
      />
      {!!error && <p className="u-text-danger">{error}</p>}
    </Modal>
  );
};

export default AddPartnerModal;
